import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { UserCog, ArrowRightLeft, UserMinus, UserPlus, Loader2 } from 'lucide-react';

/**
 * FEA Coordinator delegation panel.
 *
 * - CRD / Chairman / Admin see: Assign / Hand over / Remove buttons.
 * - The currently-delegated Coordinator sees: Hand over button only.
 * - Other roles see a read-only "no coordinator" or "Coordinator: X" line.
 * - Only renders for FEA requests.
 */
export const FeaCoordinatorPanel = ({ request, currentUser, onChanged }) => {
  const [coords, setCoords] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const [pickedId, setPickedId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!request || request.type !== 'FEA') return null;

  const userRole = currentUser?.role;
  const userId = currentUser?.id;
  const currentCoordId = request.fea_coordinator_user_id || null;
  const currentCoordName = request.fea_coordinator_name || null;
  const currentCoordAt = request.fea_coordinator_assigned_at || null;

  const isAdminLevel = ['CRD', 'CHAIRMAN', 'ADMIN'].includes(userRole);
  const isCurrentCoord = userRole === 'COORDINATOR' && currentCoordId === userId;

  const canAssign = isAdminLevel && !currentCoordId;
  const canHandover = currentCoordId && (isAdminLevel || isCurrentCoord);
  const canRemove = isAdminLevel && !!currentCoordId;
  const canSeePanel = isAdminLevel || isCurrentCoord || !!currentCoordId; // others see read-only line

  if (!canSeePanel) return null;

  const loadList = async () => {
    setLoadingList(true);
    try {
      const res = await api.get(`/requests/${request.id}/eligible-fea-coordinators`);
      setCoords(res.data || []);
    } catch (err) {
      toast.error('Could not load coordinators');
    } finally {
      setLoadingList(false);
    }
  };

  const openAssign = () => { setPickedId(''); loadList(); setAssignOpen(true); };
  const openHandover = () => { setPickedId(''); setReason(''); loadList(); setHandoverOpen(true); };
  const openRemove = () => setRemoveOpen(true);

  const submitAssign = async () => {
    if (!pickedId) { toast.error('Pick a coordinator'); return; }
    setSubmitting(true);
    try {
      await api.post(`/requests/${request.id}/fea-assign-coordinator`, { coordinator_user_id: pickedId });
      toast.success('FEA Coordinator assigned');
      setAssignOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to assign');
    } finally { setSubmitting(false); }
  };

  const submitHandover = async () => {
    if (!pickedId) { toast.error('Pick a coordinator'); return; }
    setSubmitting(true);
    try {
      await api.post(`/requests/${request.id}/fea-handover-coordinator`, {
        new_coordinator_user_id: pickedId,
        reason: reason.trim(),
      });
      toast.success('FEA Coordinator handed over');
      setHandoverOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Handover failed');
    } finally { setSubmitting(false); }
  };

  const submitRemove = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/requests/${request.id}/fea-coordinator`);
      toast.success('FEA Coordinator removed');
      setRemoveOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Card data-testid="fea-coordinator-panel" className="border-blue-100">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCog className="w-5 h-5 text-blue-700" />
          FEA Coordinator
          {currentCoordId && (
            <Badge variant="secondary" className="ml-2">Delegated</Badge>
          )}
        </CardTitle>
        <CardDescription>
          CRD can delegate this FEA&apos;s routing &amp; consultant assignment to a Coordinator
          as a second pair of hands. Final decisions still rest with CRD.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {currentCoordId ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900" data-testid="fea-coord-name">
                  {currentCoordName || 'Coordinator'}
                </div>
                {currentCoordAt && (
                  <div className="text-xs text-slate-500">
                    Assigned {new Date(currentCoordAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {canHandover && (
                <Button size="sm" variant="outline" onClick={openHandover} data-testid="fea-coord-handover-btn">
                  <ArrowRightLeft className="w-4 h-4 mr-1" /> Hand over
                </Button>
              )}
              {canRemove && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={openRemove}
                  className="text-red-700 hover:bg-red-50"
                  data-testid="fea-coord-remove-btn"
                >
                  <UserMinus className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              No coordinator assigned. CRD is handling routing directly.
            </p>
            {canAssign && (
              <Button size="sm" onClick={openAssign} data-testid="fea-coord-assign-btn">
                <UserPlus className="w-4 h-4 mr-1" /> Assign Coordinator
              </Button>
            )}
          </div>
        )}
      </CardContent>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={(o) => !submitting && setAssignOpen(o)}>
        <DialogContent data-testid="fea-coord-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign FEA Coordinator</DialogTitle>
            <DialogDescription>
              Pick a Coordinator to handle routing &amp; consultant assignment for this FEA.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto" role="radiogroup">
            {loadingList ? (
              <div className="text-sm text-slate-500 p-2"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading…</div>
            ) : coords.length === 0 ? (
              <div className="text-sm text-slate-500 p-2">No active coordinators found.</div>
            ) : (
              coords.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 border rounded-lg p-2 cursor-pointer transition ${pickedId === c.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                  data-testid={`fea-coord-pick-${c.id}`}
                >
                  <input
                    type="radio"
                    name="fea-coord-pick"
                    value={c.id}
                    checked={pickedId === c.id}
                    onChange={() => setPickedId(c.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.email}</div>
                  </div>
                </label>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitAssign} disabled={submitting || !pickedId} data-testid="fea-coord-assign-confirm">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Assigning…</> : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Handover dialog */}
      <Dialog open={handoverOpen} onOpenChange={(o) => !submitting && setHandoverOpen(o)}>
        <DialogContent data-testid="fea-coord-handover-dialog">
          <DialogHeader>
            <DialogTitle>Hand over FEA Coordinator role</DialogTitle>
            <DialogDescription>
              Currently held by <strong>{currentCoordName || 'Coordinator'}</strong>. Pick the new coordinator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto" role="radiogroup">
            {loadingList ? (
              <div className="text-sm text-slate-500 p-2"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading…</div>
            ) : (
              coords
                .filter((c) => c.id !== currentCoordId)
                .map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 border rounded-lg p-2 cursor-pointer transition ${pickedId === c.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                  >
                    <input
                      type="radio"
                      name="fea-coord-handover-pick"
                      value={c.id}
                      checked={pickedId === c.id}
                      onChange={() => setPickedId(c.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">{c.email}</div>
                    </div>
                  </label>
                ))
            )}
          </div>
          <div className="mt-3">
            <Label htmlFor="handover-reason">Reason (optional)</Label>
            <Textarea
              id="handover-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. going on leave, workload reassignment…"
              rows={2}
              data-testid="fea-coord-handover-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoverOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitHandover} disabled={submitting || !pickedId} data-testid="fea-coord-handover-confirm">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Handing over…</> : 'Hand over'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirm */}
      <Dialog open={removeOpen} onOpenChange={(o) => !submitting && setRemoveOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove FEA Coordinator?</DialogTitle>
            <DialogDescription>
              <strong>{currentCoordName || 'The current coordinator'}</strong> will lose access to
              this FEA. CRD will take back direct routing ownership. You can re-assign anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveOpen(false)} disabled={submitting}>Cancel</Button>
            <Button variant="destructive" onClick={submitRemove} disabled={submitting} data-testid="fea-coord-remove-confirm">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Removing…</> : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
